import { z } from 'zod'

import { funeralHallSummary } from './funeralHall.js'

import { digitalWreathDispatchView, wreathDesignId } from './digitalWreath.js'

import { occasionType, type OccasionType } from './product.js'
import { paymentView } from './payment.js'

/**
 * 주문 생성·접수 API 계약 (BIZ-6).
 *
 * 설계 근거: 명세서 2장(발번) · 5.3(주문) · 5.4(스냅샷).
 *
 * 핵심 두 가지:
 *  1. **유형별 필드 격리** — 요청을 occasionType로 분기하는 판별 유니온으로 만든다.
 *     근조 주문에 축하 필드(eventType 등)를 애초에 담을 수 없다. DB의
 *     `*_fields_scoped` CHECK와 같은 규칙을 입력 단계에서 먼저 막는다.
 *  2. **접수 시점 필수** — 임시저장(DRAFT)에는 필수가 없고, 접수(submit)에만
 *     필수 항목이 채워져야 한다. DB CHECK(`submitted_requires_*`)와 동일한 규칙을
 *     submit일 때만 superRefine으로 강제해, DB CHECK(500)를 400으로 앞당긴다.
 */

// ── 작은 조각 ──────────────────────────────────────────────

/** 폼이 빈 문자열로 보내는 선택 항목을 undefined로 정규화한다(→ DB NULL). */
function optionalText(max: number) {
  return z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().max(max).optional(),
  )
}

/** 리본 문구: 30자 이하, 줄바꿈 불가 (DB `ribbon_lineN_rule`). */
const ribbonLine = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  z
    .string()
    .max(30, '리본 문구는 30자 이하여야 합니다')
    .regex(/^[^\n\r]*$/, '리본 문구에는 줄바꿈을 넣을 수 없습니다')
    .optional(),
)

/** 카드 메시지: 100자 이하·최대 3줄 (DB `card_message_rule`). */
const cardMessage = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  z
    .string()
    .max(100, '카드 메시지는 100자 이하여야 합니다')
    .refine((v) => v.split('\n').length <= 3, '카드 메시지는 3줄 이하여야 합니다')
    .optional(),
)

export const desiredTimeSlot = z.enum(['BEFORE_12', 'BEFORE_14', 'BEFORE_16', 'ANYTIME'])
export type DesiredTimeSlot = z.infer<typeof desiredTimeSlot>

export const intakeChannel = z.enum(['WEB', 'PHONE', 'SMS', 'KAKAO', 'EMAIL'])
export type IntakeChannel = z.infer<typeof intakeChannel>

// ── 공통 필드 ──────────────────────────────────────────────
// 모든 유형이 공유한다. 접수 필수 여부는 아래 superRefine에서 판단.

const commonFields = {
  /** true면 생성과 동시에 접수(발번·스냅샷). 기본값은 임시저장. */
  submit: z.boolean().optional().default(false),

  // 포인트 선택(BIZ-74). 주문마다 적립(EARN=정가 결제+적립) 또는 할인(DISCOUNT=결제금액 즉시 할인) 택1.
  // 없으면 적립. 할인율·적립율은 전역 기본 + 회사 오버라이드. 1P=1원.
  pointOption: z.enum(['EARN', 'DISCOUNT', 'REDEEM']).optional(),

  // 수신자 디지털 카드(BIZ-86). 스킨을 고르면 배송완료 때 수신자에게 카드 링크가 나간다.
  // 기존 cardMessage(GIFT 실물 카드 문구)와 별개 축이라 digital 접두로 구분한다.
  digitalCardSkinId: z.number().int().positive().optional(),
  digitalCardMessage: optionalText(300),

  // 디지털 화환(BIZ-126, 옛 이름 '디지털 근조기'). 제휴 장례식장 배송 시 부고장·장례식장 화면에 함께 올린다.
  // 값은 디자인 id — 문구·보낸 분은 별도 저장 없이 주문의 리본 필드를 그대로 쓴다.
  digitalWreathDesign: wreathDesignId.optional(),

  // 상품 선택 (카탈로그의 상품). 스냅샷 원천.
  // 옵션(product_option)은 아직 카탈로그에 노출되지 않아 MVP 주문 경로에서 빼둔다.
  // 카탈로그가 옵션을 내보내면 option_id 스냅샷과 접수 시 재확정을 함께 붙인다(별도 이슈).
  productId: z.number().int().positive(),
  quantity: z.number().int().positive().max(999).optional().default(1),

  // 발신자. 프리셋을 고르거나 직접 입력한다. 접수 시 둘 중 하나는 있어야 한다.
  senderPresetId: z.number().int().positive().optional(),
  senderName: optionalText(30),

  // 거래처(선택, BIZ-49). 등록한 거래처를 고르거나(partnerId) 자유 입력한다(partnerName).
  // partnerId가 있으면 서버가 그 거래처 이름을 스냅샷하고, 없으면 partnerName 텍스트를 스냅샷한다.
  partnerId: z.number().int().positive().optional(),
  partnerName: optionalText(100),

  // 장례식장 연결(BIZ-90). 피커·파서·최근 배송지가 고른 명부 id. 없거나 비활성이면 서버가
  // deliveryPlaceName(+주소)으로 다시 resolve하고, 그래도 없으면 미연결(자유 입력 유지). 근조 전용.
  funeralHallId: z.number().int().positive().nullable().optional(),

  // 배송 공통
  deliveryAddress: optionalText(300),
  deliveryAddressDetail: optionalText(200),
  deliveryZipcode: optionalText(10),
  deliveryPlaceName: optionalText(100),
  desiredDate: z.string().date().optional(),
  desiredTimeSlot: desiredTimeSlot.optional(),
  deliveryNote: optionalText(500),
  recipientPhone: optionalText(30),

  companyMemo: optionalText(1000),

  // 대리 주문 (운영자 전용, 기획서 9.4). 기업 사용자가 보내면 서버에서 무시한다.
  intakeChannel: intakeChannel.optional(),
  requesterName: optionalText(50),
  requesterPhone: optionalText(30),
  intakeFileUrl: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().url().max(500).optional(),
  ),
}

// ── 유형별 스키마 ──────────────────────────────────────────

const funeralOrder = z.object({
  occasionType: z.literal('FUNERAL'),
  ...commonFields,
  mortuaryRoom: optionalText(100),
  deceasedName: optionalText(50),
  mournerName: optionalText(50),
  funeralAt: z.string().date().optional(),
  ribbonLine1: ribbonLine,
  ribbonLine2: ribbonLine,
})

const celebrationOrder = z.object({
  occasionType: z.literal('CELEBRATION'),
  ...commonFields,
  eventType: optionalText(50),
  eventName: optionalText(100),
  eventAt: z.string().datetime({ offset: true }).optional(),
  eventTarget: optionalText(50),
  ribbonLine1: ribbonLine,
  ribbonLine2: ribbonLine,
})

const giftOrder = z.object({
  occasionType: z.literal('GIFT'),
  ...commonFields,
  recipientName: optionalText(50),
  recipientOrg: optionalText(100),
  recipientDept: optionalText(50),
  recipientTitle: optionalText(50),
  cardMessage,
})

/**
 * 접수 시 필수 항목을 강제한다. DB CHECK와 정확히 같은 규칙이다:
 *  - 공통: 배송지·희망일·발신자
 *  - 근조: 빈소, (고인명 또는 상주명), 리본1
 *  - 축하: 행사종류, 행사대상자, 리본1
 *  - 난·기타: 수령인명, 수령처, 카드메시지
 */
function enforceSubmitRequired(
  data: { submit?: boolean; occasionType: OccasionType } & Record<string, unknown>,
  ctx: z.RefinementCtx,
) {
  if (!data.submit) return

  const require = (path: string, present: boolean, message: string) => {
    if (!present) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message })
  }
  const has = (k: string) => data[k] !== undefined && data[k] !== null && data[k] !== ''

  require('deliveryAddress', has('deliveryAddress'), '배송지를 입력하세요')
  require('desiredDate', has('desiredDate'), '희망 배송일을 입력하세요')
  // 디지털 카드(BIZ-86)는 수신자 휴대폰으로 링크를 보내므로 카드를 골랐으면 번호가 필요하다.
  if (data['digitalCardSkinId'] !== undefined) {
    require('recipientPhone', has('recipientPhone'), '디지털 카드를 보내려면 수신자 휴대폰을 입력하세요')
  }
  // 디지털 화환(BIZ-126)은 근조 전용 — DB 스코프 CHECK 전에 계약에서 거른다.
  if (data['digitalWreathDesign'] !== undefined && data.occasionType !== 'FUNERAL') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['digitalWreathDesign'],
      message: '디지털 화환은 근조 화환에서만 보낼 수 있습니다',
    })
  }
  require(
    'senderName',
    has('senderName') || data['senderPresetId'] !== undefined,
    '발신자를 지정하세요',
  )

  if (data.occasionType === 'FUNERAL') {
    require('mortuaryRoom', has('mortuaryRoom'), '빈소를 입력하세요')
    require(
      'deceasedName',
      has('deceasedName') || has('mournerName'),
      '고인명 또는 상주명을 입력하세요',
    )
    require('ribbonLine1', has('ribbonLine1'), '리본 문구를 입력하세요')
  } else if (data.occasionType === 'CELEBRATION') {
    require('eventType', has('eventType'), '행사 종류를 입력하세요')
    require('eventTarget', has('eventTarget'), '행사 대상자를 입력하세요')
    require('ribbonLine1', has('ribbonLine1'), '리본 문구를 입력하세요')
  } else {
    require('recipientName', has('recipientName'), '수령인명을 입력하세요')
    require('recipientOrg', has('recipientOrg'), '수령처를 입력하세요')
    require('cardMessage', has('cardMessage'), '카드 메시지를 입력하세요')
  }
}

export const createOrderRequest = z
  .discriminatedUnion('occasionType', [funeralOrder, celebrationOrder, giftOrder])
  .superRefine(enforceSubmitRequired)
export type CreateOrderRequest = z.infer<typeof createOrderRequest>

/**
 * 발주 후 본문 편집 시 업체 재통지 방법 (BIZ-50). order_change_log.notify_method와 같은 값.
 * 실제 알림톡 자동 발송(BIZ-17)은 사용자 트랙이라, 지금은 운영자가 통지한 방법을 기록만 한다.
 */
export const vendorNotifyMethod = z.enum(['PHONE', 'KAKAO', 'SMS', 'SYSTEM'])
export type VendorNotifyMethod = z.infer<typeof vendorNotifyMethod>

// ── 응답 ───────────────────────────────────────────────────

/**
 * 주문에 확정된 판매 스냅샷 (order_item). 접수 시점에 고정되며 이후
 * 상품 마스터가 바뀌어도 흔들리지 않는다 (명세서 5.4). 공급가·수익은 없다.
 */
export const orderItemSnapshot = z.object({
  productId: z.number().int(),
  productName: z.string(),
  category: z.string(),
  spec: z.string().nullable(),
  quantity: z.number().int(),
  unitPrice: z.number().int(),
  optionName: z.string().nullable(),
  optionPrice: z.number().int(),
  extraDeliveryFee: z.number().int(),
  discount: z.number().int(),
  totalAmount: z.number().int(),
})
export type OrderItemSnapshot = z.infer<typeof orderItemSnapshot>

/** 기업·운영자가 보는 주문 상세. 공급가·업체 메모 등 내부 필드는 포함하지 않는다. */
export const orderResponse = z.object({
  id: z.number().int(),
  orderNo: z.string().nullable(),
  companyId: z.number().int(),
  occasionType,
  status: z.string(),
  /** 내부 상태에서 파생한 기업 노출 문구 (명세서 3.2). */
  companyFacingStatus: z.string(),
  /**
   * 확인 필요(INFO_REQUIRED)일 때 운영자가 남긴 요청 메시지 + 요청 시각. 그 외 상태면 null.
   * 기업이 무엇을 고쳐야 하는지 보고 응답할 수 있게 상세에 싣는다(BIZ-58).
   */
  infoRequest: z.object({ message: z.string(), requestedAt: z.string() }).nullable(),
  vendorId: z.number().int().nullable(),
  submittedAt: z.string().nullable(),
  createdAt: z.string(),

  // 배송 공통
  deliveryAddress: z.string().nullable(),
  deliveryAddressDetail: z.string().nullable(),
  deliveryZipcode: z.string().nullable(),
  deliveryPlaceName: z.string().nullable(),
  desiredDate: z.string().nullable(),
  desiredTimeSlot: z.string().nullable(),
  deliveryNote: z.string().nullable(),
  recipientPhone: z.string().nullable(),
  senderName: z.string().nullable(),
  /** 거래처 이름 스냅샷 (BIZ-49). partnerId로 골랐든 자유 입력이든 주문 시점 이름. */
  partnerName: z.string().nullable(),

  // 근조
  mortuaryRoom: z.string().nullable(),
  deceasedName: z.string().nullable(),
  mournerName: z.string().nullable(),
  funeralAt: z.string().nullable(),
  // 축하
  eventType: z.string().nullable(),
  eventName: z.string().nullable(),
  eventAt: z.string().nullable(),
  eventTarget: z.string().nullable(),
  // 난·기타
  recipientName: z.string().nullable(),
  recipientOrg: z.string().nullable(),
  recipientDept: z.string().nullable(),
  recipientTitle: z.string().nullable(),
  // 문구
  ribbonLine1: z.string().nullable(),
  ribbonLine2: z.string().nullable(),
  cardMessage: z.string().nullable(),

  // 수신자 디지털 카드(BIZ-86). token은 배송완료 발송 후에만 채워진다.
  digitalCardSkinId: z.number().int().nullable().optional(),
  digitalCardMessage: z.string().nullable().optional(),
  digitalCardToken: z.string().nullable().optional(),
  digitalCardSentAt: z.string().nullable().optional(),

  // 디지털 화환(BIZ-126)
  digitalWreathDesign: z.string().nullable().optional(),
  // 디지털 화환 송출 상태(BIZ-125). 상세·수정 응답의 기업·운영자 뷰에서만, 송출 이력이 없으면 null.
  digitalWreathDispatch: digitalWreathDispatchView.nullable().optional(),

  /** 연결된 장례식장(BIZ-90). 미연결·근조 아님이면 null. 업체 뷰엔 없다. */
  funeralHall: funeralHallSummary.nullable().optional(),

  companyMemo: z.string().nullable(),

  item: orderItemSnapshot.nullable(),
})
export type OrderResponse = z.infer<typeof orderResponse>

// ── BIZ-29: 최근 배송지 (saved_address) ────────────────────
//
// 접수(SUBMITTED)된 주문의 배송지를 기업 스코프로 모아둔 반복 주문용 목록(기획서 19.9).
// 주문서에서 최근 배송지를 눌러 배송지·상세·장소명·우편번호를 한 번에 채운다.

export const savedAddress = z.object({
  id: z.number().int(),
  placeName: z.string().nullable(),
  address: z.string(),
  addressDetail: z.string().nullable(),
  zipcode: z.string().nullable(),
  /** 접수 당시 연결된 장례식장(BIZ-90). 재사용 시 연결까지 되살린다. */
  funeralHallId: z.number().int().nullable().optional(),
  lastUsedAt: z.string(),
  useCount: z.number().int(),
})
export type SavedAddress = z.infer<typeof savedAddress>

export const savedAddressListResponse = z.object({ items: z.array(savedAddress) })
export type SavedAddressListResponse = z.infer<typeof savedAddressListResponse>

// ── BIZ-7: 목록·상세 조회 (권한 필터 + 필드 노출) ──────────
//
// 역할마다 "어떤 주문"과 "어떤 필드"를 보는지가 다르다. 필드 숨김은 응답
// 직렬화가 아니라 쿼리 단계에서 처리한다(명세서 4.2) — 도메인이 역할별 SELECT를
// 만든다. 이 계약의 스키마는 수퍼셋이고, 숨긴 필드는 응답에서 null/누락으로 나온다.

export const orderStatusValue = z.enum([
  'DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'INFO_REQUIRED', 'COST_REVIEW',
  'VENDOR_ASSIGNED', 'PO_SENT', 'VENDOR_ACCEPTED', 'VENDOR_REJECTED',
  'PREPARING', 'DELIVERING', 'DELIVERED', 'DELIVERY_ISSUE', 'CANCELLED',
])
export type OrderStatusValue = z.infer<typeof orderStatusValue>

/**
 * 기업이 목록에서 고를 수 있는 상태 = 기업 노출 상태(파생 8종). 내부 상태(status)와 달리
 * 기업에게 의미 있는 단위다. 서버가 이 라벨을 내부 상태 집합으로 역매핑해 필터한다
 * (단일 출처: order_transitions.json companyFacingStatus).
 */
export const companyFacingStatusFilter = z.enum([
  '작성 중',
  '주문 접수',
  '확인 필요',
  '준비 중',
  '배송 중',
  '배송 완료',
  // BIZ-118: 업체 거절은 '준비 중'으로 흡수(내부 churn), 배송 사고만 '배송 문제'로 노출.
  '배송 문제',
  '취소 완료',
])
export type CompanyFacingStatusFilter = z.infer<typeof companyFacingStatusFilter>

/** 목록 필터·페이지. 쿼리스트링이라 숫자는 coerce한다. */
export const orderListQuery = z.object({
  status: orderStatusValue.optional(), // 운영자·업체: 내부 상태
  companyStatus: companyFacingStatusFilter.optional(), // 기업: 노출 상태(파생)
  q: z.string().max(100).optional(), // 통합 검색 (고인명·상주명·행사대상·수령인·장례식장·발신자)
  companyId: z.coerce.number().int().positive().optional(), // 운영자만: 기업 필터
  occasionType: occasionType.optional(),
  from: z.string().date().optional(), // desired_date >= from
  to: z.string().date().optional(), // desired_date <= to
  // 임시저장(DRAFT) 제외. 쿼리스트링이라 'true'/'false' 문자열로 온다.
  excludeDrafts: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  // 정렬(운영자 목록). 미지정이면 기본(주문번호 내림차순). 컬럼은 서버 화이트리스트로 안전.
  sort: z.enum(['orderNo', 'company', 'desiredDate', 'amount', 'createdAt']).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0),
})
export type OrderListQuery = z.infer<typeof orderListQuery>

/**
 * 목록 한 줄. 역할별 필드 노출:
 *  - status(내부 상태): 운영자·업체만. 기업 응답에선 null(파생 상태만 본다).
 *  - amount(판매 총액): 기업·운영자만. 업체 응답에선 null(판매가 숨김).
 */
/**
 * 업체(배정 주문) 목록 카드의 발주 정보 묶음. 업체 행에만 채워지고, 기업·운영자
 * 행에선 null이다. 여기 담기는 값은 전부 업체 발주 화이트리스트
 * (permissions.vendorPurchaseOrderFields) 안이거나 배송 정보(기사·완료·사진 수)라 —
 * 판매가·구매 기업 신원은 절대 들어오지 않는다.
 */
export const vendorCard = z.object({
  vendorProductName: z.string(), // 업체 공급 상품명 (order_supply.vendor_product_name)
  deliveryPlaceName: z.string().nullable(),
  // 도로명 주소. 장례식장명(deliveryPlaceName)·빈소가 없는 난·기타 주문의 배송지 폴백(BIZ-32 #5).
  deliveryAddress: z.string().nullable(),
  deliveryAddressDetail: z.string().nullable(),
  mortuaryRoom: z.string().nullable(),
  supplyUnitPrice: z.number().int().nullable(), // 공급가 (order_supply)
  ribbonLine1: z.string().nullable(),
  ribbonLine2: z.string().nullable(),
  timeSlot: z.string().nullable(), // 희망 시간대 (orders.desired_time_slot)
  driverName: z.string().nullable(), // 배송 중일 때 (delivery)
  driverPhone: z.string().nullable(),
  completedAt: z.string().nullable(), // 배송 완료 시각 (delivery.completed_at)
  photoCount: z.number().int(), // 배송 사진 수 (delivery_photo)
})
export type VendorCard = z.infer<typeof vendorCard>

export const orderListItem = z.object({
  id: z.number().int(),
  orderNo: z.string().nullable(),
  // 구매 기업 신원은 업체에게 숨긴다(중개이탈 방지). 업체 응답에선 null.
  companyId: z.number().int().nullable(),
  companyName: z.string().nullable(),
  occasionType,
  status: z.string().nullable(),
  companyFacingStatus: z.string(),
  title: z.string(), // 한 줄 요약 (고인/행사대상/수령인)
  desiredDate: z.string().nullable(),
  createdAt: z.string(),
  amount: z.number().int().nullable(),
  // 결제 구분(BIZ-78) — 기업·운영자 행에만. PAID=선불 완료, UNPAID=선불 미결제, REFUNDED=선불 환불,
  // POSTPAID=후불. 업체 행·임시저장·취소/종결 등 해당 없음은 null.
  paymentBadge: z.enum(['PAID', 'UNPAID', 'REFUNDED', 'POSTPAID']).nullable().optional(),
  // 업체 행에만 채워지는 발주 카드 정보. 기업·운영자 행에선 null.
  vendorCard: vendorCard.nullable(),
})
export type OrderListItem = z.infer<typeof orderListItem>

export const orderListResponse = z.object({
  items: z.array(orderListItem),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
})
export type OrderListResponse = z.infer<typeof orderListResponse>

/**
 * 기업 주문 목록 상단 요약 카운트 (BIZ-10 목록 헤더). 기업 스코프(자기 기업만).
 *  - total: 전체 주문(임시저장 포함, 목록 '총 건수'와 같은 모수)
 *  - inProgress: 진행 중(임시저장·완료·종결·취소 제외)
 *  - attention: 확인 필요(INFO_REQUIRED·COST_REVIEW → companyFacingStatus '확인 필요')
 *  - deliveredThisMonth: 이번 달(KST) 배송 완료(billing_month 기준)
 */
export const orderSummary = z.object({
  total: z.number().int(),
  inProgress: z.number().int(),
  attention: z.number().int(),
  deliveredThisMonth: z.number().int(),
})
export type OrderSummary = z.infer<typeof orderSummary>

/**
 * 통계 탭 집계 (BIZ-46 → BIZ-91). 기간(희망일 from·to) 내 기업 주문을 돌아본다. 임시저장(작성 중)은 뺀다.
 *  - totals: 주문 수·진행 중·확인 필요·금액 합계(판매가).
 *  - byPartner: 거래처(주문에 스냅샷된 partner_name) 기준 롤업. 거래처 미지정은 수령처·장소로 폴백.
 *  - byRecipient: 개별 수신자(근조=고인 / 축하=대상 / 난·기타=받는 사람) 기준 집계.
 *  두 축 모두 횟수·금액을 상위순으로. 실제 화환 업체는 어느 축에도 노출하지 않는다.
 */
const statsBucket = z.object({
  label: z.string(),
  count: z.number().int(),
  amountSum: z.number().int(),
})
export const orderStatsResponse = z.object({
  totals: z.object({
    count: z.number().int(),
    inProgress: z.number().int(),
    attention: z.number().int(),
    amountSum: z.number().int(),
  }),
  byPartner: z.array(statsBucket),
  byRecipient: z.array(statsBucket),
})
export type OrderStatsResponse = z.infer<typeof orderStatsResponse>

/**
 * 기업 담당자 홈 대시보드 (BIZ-44). "보내기 + 챙기기" — 장부가 아니라 액션 중심.
 *  - attention: 지금 챙길 것(확인 필요·문제) 상위 몇 건.
 *  - deliveringToday/Tomorrow: 오늘/내일 배송 예정 건수(임박 인지).
 *  - deliveringSoon: 모레·글피(D-2~D-3) 배송 예정 — 예약이 다가온다는 리마인더.
 *  - recent: 최근 주문 몇 건(사람 말 식별).
 *  - frequentDestinations: 자주 보낸 곳(재주문 바로가기). 받는 곳 기준이며 화환 업체 아님.
 */
export const frequentDestination = z.object({
  placeName: z.string().nullable(),
  address: z.string(),
  addressDetail: z.string().nullable(),
  zipcode: z.string().nullable(),
  count: z.number().int(),
})
export type FrequentDestination = z.infer<typeof frequentDestination>

export const companyHomeResponse = z.object({
  attention: z.array(orderListItem),
  deliveringToday: z.number().int(),
  deliveringTomorrow: z.number().int(),
  deliveringSoon: z.number().int(),
  /** 결제 필요(선불 미결제) 주문 수(BIZ-78 후속). 홈 긴급 스트립이 노출한다. */
  unpaidCount: z.number().int(),
  /** 회사 승인상태(BIZ-68). 가입 승인만 뜻한다 — 후불 여부는 postpaidStatus(BIZ-149). */
  approvalStatus: z.string().nullable(),
  /** 후불 자격(BIZ-149). ACTIVE가 아니면 홈이 '선불 결제' 안내와 후불 신청 상태를 보인다. */
  postpaidStatus: z.string().nullable(),
  /** 가입 주체(홈 안내 문구 분기용). 기업만 '사업자 확인→후불' 안내를 보인다(BIZ-85). */
  subjectType: z.string().nullable(),
  /** 사업자 검증 상태(BIZ-85). 홈 스트립 문구 분기. */
  bizVerifyStatus: z.string().nullable(),
  /** 알림 수신번호 설정 여부 — 미설정이면 주문 알림톡이 못 나가 홈이 설정을 유도한다. */
  notifyPhoneSet: z.boolean(),
  recent: z.array(orderListItem),
  frequentDestinations: z.array(frequentDestination),
  /** 작성 중인 내 임시저장 하나(없으면 null) — 홈에서 이어서 작성 (BIZ-80 후속). */
  draft: orderListItem.nullable(),
})
export type CompanyHomeResponse = z.infer<typeof companyHomeResponse>

/**
 * 업체 배정 주문 목록 상단 요약 카운트. 업체 스코프(자기 업체에 배정된 주문만).
 *  - waiting: 접수 대기 (PO_SENT — 아직 수락 전)
 *  - inProgress: 배송 진행 (VENDOR_ACCEPTED·PREPARING·DELIVERING·DELIVERY_ISSUE)
 *  - doneThisMonth: 이번 달(KST) 완료 (DELIVERED, billing_month 기준)
 */
export const vendorSummary = z.object({
  waiting: z.number().int(),
  inProgress: z.number().int(),
  doneThisMonth: z.number().int(),
})
export type VendorSummary = z.infer<typeof vendorSummary>

/** 업체·운영자만 보는 공급(발주) 정보. 기업 응답엔 없다(공급가 숨김). */
export const orderSupplyView = z.object({
  vendorProductName: z.string(),
  quantity: z.number().int(),
  supplyUnitPrice: z.number().int(),
  extraDeliveryFeePayable: z.number().int(),
  totalPayable: z.number().int(),
})
export type OrderSupplyView = z.infer<typeof orderSupplyView>

/**
 * 상세 조회 응답(수퍼셋). 역할별로 채워지는 필드가 다르다:
 *  - status: 기업엔 null(파생 상태만).
 *  - item(판매 스냅샷): 기업·운영자만. 업체엔 null(판매가 숨김).
 *  - vendorMemo: 운영자·업체만. 기업엔 null.
 *  - supply: 운영자·업체만. 기업엔 null.
 *  - companyMemo: 기업·운영자만. 업체엔 null(orderResponse에서 상속).
 * 업체는 발주 화이트리스트(permissions.vendorPurchaseOrderFields) 밖 필드도 null이다.
 */
export const orderDetailResponse = orderResponse.extend({
  status: z.string().nullable(),
  // 구매 기업 신원은 업체에게, 배정 업체 신원은 기업에게 숨긴다(양방향 중개이탈 방지).
  companyId: z.number().int().nullable(), // 업체 응답에선 null
  // vendorId는 orderResponse에서 이미 nullable — 기업 응답에선 null로 채운다.
  vendorMemo: z.string().nullable(),
  supply: orderSupplyView.nullable(),
  // 기업 셀프편집 가능 여부. 기업 뷰에서만 채운다 — 내부 상태를 숨긴 기업이 "확인 필요"처럼
  // 편집 가능/불가가 겹치는 파생 상태에서도 정확히 판정할 수 있게 서버가 계산해 준다.
  // canCompanyEdit: 발주 전 전체 편집. canCompanyEditTextOnly: 발주~준비 중 텍스트만(배송 후 둘 다 false, BIZ-71).
  canCompanyEdit: z.boolean().optional(),
  canCompanyEditTextOnly: z.boolean().optional(),
  // 결제 축(BIZ-70/78). 기업·운영자 뷰에서만 채운다(업체엔 판매액이라 숨김).
  // paymentRequired: 가승인(선불만) 기업이고 아직 결제 완료가 아니면 true — 서버가 판정해 준다.
  paymentRequired: z.boolean().optional(),
  payment: paymentView.nullable().optional(),
  // 포인트(BIZ-74). 기업·운영자 뷰에서만. pointsEarned=이 주문으로 적립된 포인트(배송완료 후 지급).
  // pointsEarnPending=접수 때 확정한 적립 예정액(배송완료 전). 할인액은 item.discount에 있다.
  pointsEarned: z.number().int().nullable().optional(),
  pointsEarnPending: z.number().int().nullable().optional(),
  // 포인트 선택(EARN/DISCOUNT/REDEEM)과 전액결제에 사용한 포인트(REDEEM 주문).
  pointOption: z.enum(['EARN', 'DISCOUNT', 'REDEEM']).nullable().optional(),
  pointsRedeemed: z.number().int().nullable().optional(),
})
export type OrderDetailResponse = z.infer<typeof orderDetailResponse>

// ── BIZ-8: 상태 전이 ────────────────────────────────────────
//
// 상태머신(orderStateMachine/spec)을 DB·HTTP·사이드이펙트에 연결한다.
// 전이 규칙은 여기서 정하지 않는다 — 계약은 입력 형태만 정의하고, 허용 여부는
// canTransition이 판정한다.

export const orderTransitionRequest = z.object({
  to: orderStatusValue,
  /** 사유. 취소·거절·강제 전이 등에서 필수(전이마다 다름). */
  reason: z.string().max(500).optional(),
  /** 운영자 강제 전이(오기입 교정). 전이표를 우회하되 사유 필수, 이력에 forced 기록. */
  forced: z.boolean().optional().default(false),
  /** 업체 배정(→VENDOR_ASSIGNED) 시 배정할 업체. */
  vendorId: z.number().int().positive().optional(),
  /** 취소 비용. 발주(PO_SENT) 이후 취소 전이에서만 입력 가능. int4 상한을 넘기면 400(DB 오버플로 500 방지). */
  cancellationFee: z.number().int().min(0).max(2_147_483_647).optional(),
  /**
   * 발주(→PO_SENT) 시 확정할 공급 스냅샷 값 (BIZ-11). 생략하면 업체 공급 상품의
   * base_supply_price와 추가배송비 0으로 확정한다. 발주 이외의 전이에 오면 거부한다.
   */
  supplyUnitPrice: z.number().int().min(0).max(2_147_483_647).optional(),
  extraDeliveryFeePayable: z.number().int().min(0).max(2_147_483_647).optional(),
  /**
   * 배송 시작(→DELIVERING) 시 기사 정보 (BIZ-24, 설계 5.5). 모두 선택 —
   * 시작 시각(delivery.started_at)은 서버가 전이 시점(now)으로 굳히므로 입력받지 않는다.
   * DELIVERING 이외의 전이에 와도 그냥 무시한다(공급가처럼 거부까지 하진 않는다 — 정보성이라 위험이 없다).
   */
  driverName: z.string().max(100).optional(),
  driverPhone: z.string().max(30).optional(),
  deliveryEta: z.string().datetime({ offset: true }).optional(),
  startNote: z.string().max(500).optional(),
})
export type OrderTransitionRequest = z.infer<typeof orderTransitionRequest>

// ── 처리 이력 타임라인 (BIZ-27) ──────────────────────────────

/**
 * 주문 처리 이력의 한 사건. 라벨·부가설명은 **서버가 역할에 맞게 렌더**한다 —
 * 기업엔 파생 상태만, 운영자엔 내부 상태·행위자·강제 여부·공급 변경까지. 내부 상태 enum이나
 * 공급가 원값이 화면 계약으로 새지 않도록 노출 판정을 전부 서버에 둔다(명세서 4.2).
 */
export const orderHistoryEvent = z.object({
  /** 발생 시각(ISO). */
  at: z.string(),
  /** 'STATUS' 상태 전이 / 'CHANGE' 공급 스냅샷 변경(운영자만). */
  kind: z.enum(['STATUS', 'CHANGE']),
  /** 사람이 읽는 제목. 역할별로 다르다(기업=파생 상태, 운영자=내부 상태·변경 항목). */
  label: z.string(),
  /** 부가 설명(행위자·강제·사유·변경 전후). 없으면 null. */
  sublabel: z.string().nullable(),
  /** 강제 전이 표시용(운영자). 아니면 false. */
  forced: z.boolean(),
})
export type OrderHistoryEvent = z.infer<typeof orderHistoryEvent>

export const orderHistoryResponse = z.object({ events: z.array(orderHistoryEvent) })
export type OrderHistoryResponse = z.infer<typeof orderHistoryResponse>

// ── BIZ-11: 발주 후 공급 스냅샷 변경 ────────────────────────
//
// 발주(PO_SENT) 이후 공급가가 협의로 바뀌면 order_supply를 직접 갱신하고 변경
// 전후 값을 order_change_log에 남긴다(명세서 5.4·19.7). 리비전 테이블은 MVP에 과하다.
// 운영자 전용이며 상태 전이가 아니다 — 주문 상태는 그대로 두고 스냅샷만 고친다.

/** 업체에 발주 변경을 전달한 방식 (order_change_log.notify_method). */
export const poNotifyMethod = z.enum(['PHONE', 'KAKAO', 'SMS', 'SYSTEM'])
export type PoNotifyMethod = z.infer<typeof poNotifyMethod>

export const purchaseOrderAmendRequest = z
  .object({
    supplyUnitPrice: z.number().int().min(0).max(2_147_483_647).optional(),
    extraDeliveryFeePayable: z.number().int().min(0).max(2_147_483_647).optional(),
    /** 업체에 변경을 어떻게 전달했는지. 주면 전달 이력(notified_vendor_*)을 함께 남긴다. */
    notifyMethod: poNotifyMethod.optional(),
  })
  .refine((d) => d.supplyUnitPrice !== undefined || d.extraDeliveryFeePayable !== undefined, {
    message: '변경할 공급가 또는 추가배송비를 입력하세요',
    path: ['supplyUnitPrice'],
  })
export type PurchaseOrderAmendRequest = z.infer<typeof purchaseOrderAmendRequest>

export const OrderErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  COMPANY_NOT_FOUND: 'COMPANY_NOT_FOUND',
  CONTRACT_INACTIVE: 'CONTRACT_INACTIVE', // 계약이 ACTIVE가 아닌 기업의 접수 차단
  PRODUCT_NOT_IN_CATALOG: 'PRODUCT_NOT_IN_CATALOG',
  PRODUCT_NOT_SELLABLE: 'PRODUCT_NOT_SELLABLE',
  /** 연결된 장례식장이 이 상품(또는 화환 전체)을 받지 않는다(BIZ-90 S3). */
  PRODUCT_BLOCKED_AT_HALL: 'PRODUCT_BLOCKED_AT_HALL',
  SENDER_PRESET_INVALID: 'SENDER_PRESET_INVALID',
  INSUFFICIENT_POINTS: 'INSUFFICIENT_POINTS', // 포인트 전액결제(REDEEM)에 쓸 사용가능 잔액 부족 (BIZ-74)
  PARTNER_INVALID: 'PARTNER_INVALID', // 거래처가 이 회사 것이 아니거나 없음 (BIZ-49)
  CARD_SKIN_INVALID: 'CARD_SKIN_INVALID', // 디지털 카드 스킨이 없거나 비활성·유형 불일치 (BIZ-86)
  ORDER_NOT_FOUND: 'ORDER_NOT_FOUND',
  NOT_DRAFT: 'NOT_DRAFT',
  NOT_EDITABLE: 'NOT_EDITABLE', // 운영자 본문 편집 불가 상태(배송 시작 이후 등) (BIZ-37·50)
  NOTIFY_METHOD_REQUIRED: 'NOTIFY_METHOD_REQUIRED', // 발주 후 편집은 업체 재통지 방법이 필수 (BIZ-50)
  POST_PO_ITEM_LOCKED: 'POST_PO_ITEM_LOCKED', // 발주 후엔 상품·수량·유형 변경 불가(텍스트만) (BIZ-50)
  COMPANY_EDIT_LOCKED: 'COMPANY_EDIT_LOCKED', // 기업 셀프편집은 발주(준비) 전까지만 — 이후 변경은 전화(운영자)
  REQUIRED_FIELDS_MISSING: 'REQUIRED_FIELDS_MISSING',
  FORBIDDEN: 'FORBIDDEN',
  // 상태 전이 (BIZ-8)
  TRANSITION_NOT_ALLOWED: 'TRANSITION_NOT_ALLOWED', // 전이표에 없는 간선
  REASON_REQUIRED: 'REASON_REQUIRED',
  TRANSITION_BLOCKED: 'TRANSITION_BLOCKED', // 조건부 차단(청구 확정 후 되돌리기 등)
  STALE_STATUS: 'STALE_STATUS', // 그 사이 상태가 바뀜(동시 전이)
  USE_SUBMIT_ENDPOINT: 'USE_SUBMIT_ENDPOINT', // DRAFT→SUBMITTED는 접수 엔드포인트로
  VENDOR_REQUIRED: 'VENDOR_REQUIRED', // 업체 배정에 vendorId 필요
  VENDOR_NOT_FOUND: 'VENDOR_NOT_FOUND',
  VENDOR_INACTIVE: 'VENDOR_INACTIVE', // 계약 정지·종료된 업체는 배정 불가 (BIZ-23)
  SUPPLY_NOT_FOUND: 'SUPPLY_NOT_FOUND', // 발주할 업체 공급 상품이 없음
  PHOTO_REQUIRED: 'PHOTO_REQUIRED', // 배송완료엔 사진 1장 이상(운영자는 사유로 예외)
  CANCEL_FEE_NOT_ALLOWED: 'CANCEL_FEE_NOT_ALLOWED', // 발주 전 취소엔 비용 입력 불가
  SUPPLY_FIELDS_NOT_ALLOWED: 'SUPPLY_FIELDS_NOT_ALLOWED', // 공급가·추가배송비는 발주(PO_SENT) 전이에만
  // 발주 후 변경 (BIZ-11)
  PO_NOT_SENT: 'PO_NOT_SENT', // 발주 전이라 변경할 공급 스냅샷이 없음
  SUPPLY_AMEND_BLOCKED: 'SUPPLY_AMEND_BLOCKED', // 취소·청구 확정된 주문의 공급 스냅샷은 못 고침
  // 각 필드는 int4 상한 안이지만 total_payable(공급가*수량+추가배송비) 합/곱이 int4를 넘김. DB 22003(500) 대신 400.
  AMOUNT_OUT_OF_RANGE: 'AMOUNT_OUT_OF_RANGE',
} as const
export type OrderErrorCode = (typeof OrderErrorCode)[keyof typeof OrderErrorCode]
